/**
 * Input sanitization utilities for frontend
 *
 * Sanitizes user input to prevent XSS attacks before sending to backend.
 * The backend also has its own sanitization layer (middleware/sanitizer.go).
 */

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - Input string to sanitize
 * @returns {string} Sanitized string
 */
export function escapeHtml(str) {
  if (!str || typeof str !== "string") return str;
  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return str.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * Strip HTML tags from string
 * @param {string} str - Input string
 * @returns {string} String without HTML tags
 */
export function stripHtml(str) {
  if (!str || typeof str !== "string") return str;
  return str.replace(/<[^>]*>/g, "");
}

/**
 * Sanitize text input — strips HTML tags and trims whitespace
 * Use this for form inputs before sending to backend.
 * @param {string} str - Raw user input
 * @returns {string} Sanitized string
 */
export function sanitizeInput(str) {
  if (!str || typeof str !== "string") return str;
  return stripHtml(str).trim();
}

/**
 * Sanitize an object's string values recursively
 * @param {object} obj - Object with values to sanitize
 * @returns {object} Object with sanitized string values
 */
export function sanitizeObject(obj) {
  if (!obj || typeof obj !== "object") return obj;

  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      result[key] = sanitizeInput(value);
    } else if (typeof value === "object" && value !== null) {
      result[key] = sanitizeObject(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}
